import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plane, LogOut, Plus, Pencil, Trash2, X, Upload, Package, Ticket, Settings as SettingsIcon, Loader2, ExternalLink,
  ChevronLeft, ChevronRight, Star, ShoppingCart, Users, Boxes, Bell, AlertTriangle, BarChart3, Palette, Layers, MessageSquare,
  Image as ImageIcon, Quote, CreditCard, Download,
} from "lucide-react";
import { api, resolveImg, formatPrice, formatApiError, getImages } from "@/lib/api";
import { BrandPanel, CouponsPanel, CollectionsPanel, ReviewsPanel, AnalyticsPanel, ReturnsPanel, SectionsManager, BannersPanel, TestimonialsPanel, PaymentsPanel, ExportPanel } from "@/pages/AdminPanels";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { AirplaneLogo } from "@/components/motifs";
import { toast } from "sonner";

const CATEGORIES = ["Rings", "Necklaces", "Earrings", "Bracelets", "Anklets"];
const SEGMENTS = ["Anti-Tarnish", "Oxidised"];
const ORDER_STATUSES = ["Order Confirmed", "Packed", "Dispatched", "In Transit", "Out for Delivery", "Delivered", "Cancelled"];

/* ---------------------------------------------------------------- LOGIN */
function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email, password);
    if (!res.ok) setErr(res.error);
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#2C221E] px-4 grain">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="relative w-full max-w-sm rounded-2xl border border-amber-600/30 bg-[#3D302A] p-8 shadow-2xl"
      >
        <div className="flex flex-col items-center">
          <AirplaneLogo size={52} />
          <h1 className="mt-3 font-serif text-3xl text-[#F4E7C5]">PRIYARAA</h1>
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber-500/70">Crew Access Only</p>
        </div>
        <div className="mt-8 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-[#2C221E] px-3 py-2.5 text-cream outline-none focus:border-amber-500"
              placeholder="admin@priyaraa.com"
              required
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-400">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/10 bg-[#2C221E] px-3 py-2.5 text-cream outline-none focus:border-amber-500"
              placeholder="••••••••"
              required
            />
          </div>
          {err && <p data-testid="admin-login-error" className="text-sm text-red-400">{err}</p>}
          <button
            data-testid="admin-login-button"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient py-3 font-semibold uppercase tracking-wider text-stone-900 disabled:opacity-60"
          >
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Plane size={18} />} Board Dashboard
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------------------------------------------------------------- MULTI-IMAGE MANAGER */
function MultiImageManager({ images = [], onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/upload/batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...(images || []), ...data.images.map((i) => i.url)]);
      toast.success(`${data.images.length} photo(s) uploaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const move = (i, dir) => {
    const arr = [...images];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const makeCover = (i) => {
    const arr = [...images];
    const [x] = arr.splice(i, 1);
    arr.unshift(x);
    onChange(arr);
  };
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const addUrl = () => {
    if (urlInput.trim()) {
      onChange([...(images || []), urlInput.trim()]);
      setUrlInput("");
    }
  };

  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Product Photos</label>
      <p className="mb-2 mt-0.5 text-[11px] text-stone-400">
        Upload front, back, side, close-up & model shots. First photo is the cover — use arrows to reorder, ★ to set cover.
      </p>
      <div className="grid grid-cols-4 gap-2">
        {(images || []).map((im, i) => (
          <div
            key={i}
            data-testid={`product-image-${i}`}
            className="group/thumb relative aspect-square overflow-hidden rounded-lg border border-[#E7DFD3] bg-[#F3EDE2]"
          >
            <img src={resolveImg(im)} alt={`view ${i + 1}`} className="h-full w-full object-cover" />
            {i === 0 && (
              <span className="absolute left-1 top-1 rounded bg-amber-600 px-1 py-0.5 font-mono text-[7px] font-bold uppercase tracking-wider text-white">
                Cover
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-black/55 py-1 opacity-0 transition-opacity group-hover/thumb:opacity-100">
              <button type="button" data-testid={`image-left-${i}`} onClick={() => move(i, -1)} className="text-white/90 hover:text-white" title="Move left"><ChevronLeft size={14} /></button>
              <button type="button" data-testid={`image-cover-${i}`} onClick={() => makeCover(i)} className="text-amber-300 hover:text-amber-200" title="Set as cover"><Star size={12} /></button>
              <button type="button" data-testid={`image-right-${i}`} onClick={() => move(i, 1)} className="text-white/90 hover:text-white" title="Move right"><ChevronRight size={14} /></button>
              <button type="button" data-testid={`image-delete-${i}`} onClick={() => remove(i)} className="text-red-300 hover:text-red-200" title="Delete"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="product-images-upload-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-[#E7DFD3] text-stone-400 transition-colors hover:border-amber-500 hover:text-amber-600 disabled:opacity-60"
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          <span className="text-[9px] uppercase tracking-wide">{uploading ? "Uploading" : "Add photos"}</span>
        </button>
      </div>
      <div className="mt-2 flex gap-2">
        <input
          data-testid="product-image-url-input"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="or paste an image URL"
          className="flex-1 rounded-lg border border-[#E7DFD3] bg-white px-3 py-1.5 text-xs outline-none focus:border-amber-500"
        />
        <button type="button" data-testid="product-image-url-add" onClick={addUrl} className="rounded-lg border border-[#E7DFD3] px-3 py-1.5 text-xs text-stone-600 hover:border-amber-500">Add</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
    </div>
  );
}

/* ---------------------------------------------------------------- SINGLE IMAGE FIELD (hero) */
function SingleImageField({ value, onChange, testid, label = "Hero Image" }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">{label}</label>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border border-[#E7DFD3] bg-[#F3EDE2]">
          {value ? (
            <img src={resolveImg(value)} alt="hero preview" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-stone-300"><Package size={22} /></div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
          <button type="button" data-testid={`${testid}-upload-btn`} onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 rounded-lg border border-[#E7DFD3] bg-white px-3 py-2 text-sm text-stone-700 hover:border-amber-500 disabled:opacity-60">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {uploading ? "Uploading..." : value ? "Replace image" : "Upload image"}
          </button>
          {value && (
            <button type="button" data-testid={`${testid}-remove-btn`} onClick={() => onChange("")} className="inline-flex items-center gap-1 rounded-lg border border-[#E7DFD3] bg-white px-3 py-2 text-sm text-red-500 hover:border-red-300">
              <Trash2 size={14} /> Remove
            </button>
          )}
          </div>
          <input data-testid={`${testid}-url-input`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="or paste image URL" className="w-full rounded-lg border border-[#E7DFD3] bg-white px-3 py-1.5 text-xs outline-none focus:border-amber-500" />
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

/* ---------------------------------------------------------------- PRODUCT MODAL */
const EMPTY_PRODUCT = {
  name: "", category: "Rings", segment: "Anti-Tarnish", price: "", compare_at_price: "", sale_price: "", description: "",
  image_url: "", images: [], badge: "", material: "Anti-Tarnish Brass", sku: "", size: "", colour: "", collection: "",
  tags: [], occasions: [], stock: 10, low_stock_threshold: 3, featured: false, bestseller: false, new_arrival: false, status: "published",
};

function ProductModal({ product, onClose, onSaved }) {
  const [form, setForm] = useState(
    product ? { ...EMPTY_PRODUCT, ...product, images: getImages(product) } : EMPTY_PRODUCT
  );
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
        sale_price: form.sale_price ? Number(form.sale_price) : null,
        stock: Number(form.stock) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 3,
        badge: form.badge || null,
        images: form.images || [],
        tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
        occasions: Array.isArray(form.occasions) ? form.occasions : String(form.occasions || "").split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (product?.id) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="product-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-stone-900">{product ? "Edit Product" : "Add Product"}</h2>
          <button data-testid="product-modal-close" onClick={onClose} className="text-stone-400 hover:text-stone-700">
            <X size={22} />
          </button>
        </div>
        <div className="mt-5 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Name</label>
            <input data-testid="product-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Category</label>
              <select data-testid="product-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Segment</label>
              <select data-testid="product-segment-select" value={form.segment} onChange={(e) => set("segment", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500">
                {SEGMENTS.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Material</label>
            <input data-testid="product-material-input" value={form.material} onChange={(e) => set("material", e.target.value)} placeholder="e.g. Anti-Tarnish Brass, Oxidised Silver Tone" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">SKU</label>
              <input data-testid="product-sku-input" value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="PRY-R-001" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Collection</label>
              <input data-testid="product-collection-input" value={form.collection} onChange={(e) => set("collection", e.target.value)} placeholder="e.g. Voyager" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Low-stock alert</label>
              <input data-testid="product-lowstock-input" type="number" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Size</label>
              <input data-testid="product-size-input" value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="Free / 6 / 7..." className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Colour</label>
              <input data-testid="product-colour-input" value={form.colour} onChange={(e) => set("colour", e.target.value)} placeholder="Gold / Silver" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Sale ₹ (optional)</label>
              <input data-testid="product-saleprice-input" type="number" value={form.sale_price || ""} onChange={(e) => set("sale_price", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Tags (comma separated)</label>
              <input data-testid="product-tags-input" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} placeholder="minimal, gift" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Occasions (comma separated)</label>
              <input data-testid="product-occasions-input" value={Array.isArray(form.occasions) ? form.occasions.join(", ") : form.occasions} onChange={(e) => set("occasions", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} placeholder="Everyday, Festive, Gifting" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Price ₹</label>
              <input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Was ₹</label>
              <input data-testid="product-compare-input" type="number" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Stock</label>
              <input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Badge (optional)</label>
            <input data-testid="product-badge-input" value={form.badge || ""} onChange={(e) => set("badge", e.target.value)} placeholder="e.g. ANTI-TARNISH, OXIDISED, BESTSELLER, NEW" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Description</label>
            <textarea data-testid="product-desc-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 w-full resize-none rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <MultiImageManager images={form.images} onChange={(imgs) => set("images", imgs)} />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2">
              <input data-testid="product-featured-checkbox" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-amber-600" />
              <span className="text-sm text-stone-700">Featured</span>
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="product-bestseller-checkbox" type="checkbox" checked={form.bestseller} onChange={(e) => set("bestseller", e.target.checked)} className="h-4 w-4 accent-amber-600" />
              <span className="text-sm text-stone-700">Bestseller</span>
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="product-newarrival-checkbox" type="checkbox" checked={form.new_arrival} onChange={(e) => set("new_arrival", e.target.checked)} className="h-4 w-4 accent-amber-600" />
              <span className="text-sm text-stone-700">New Arrival</span>
            </label>
            <label className="ml-auto flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Status</span>
              <select data-testid="product-status-select" value={form.status} onChange={(e) => set("status", e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </label>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#E7DFD3] py-2.5 text-sm text-stone-600">Cancel</button>
          <button data-testid="product-save-button" onClick={save} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full gold-gradient py-2.5 text-sm font-semibold text-stone-900 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- OFFER MODAL */
const EMPTY_OFFER = { title: "", promo_code: "", discount: "", description: "", gate: "A1", flight_class: "FIRST CLASS", expiry: "", active: true };

function OfferModal({ offer, onClose, onSaved }) {
  const [form, setForm] = useState(offer ? { ...EMPTY_OFFER, ...offer } : EMPTY_OFFER);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (offer?.id) await api.put(`/offers/${offer.id}`, form);
      else await api.post("/offers", form);
      toast.success("Offer saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="offer-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl text-stone-900">{offer ? "Edit Offer" : "Add Offer"}</h2>
          <button data-testid="offer-modal-close" onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={22} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Title</label>
              <input data-testid="offer-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Discount</label>
              <input data-testid="offer-discount-input" value={form.discount} onChange={(e) => set("discount", e.target.value)} placeholder="10% OFF" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Promo Code</label>
            <input data-testid="offer-code-input" value={form.promo_code} onChange={(e) => set("promo_code", e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Description</label>
            <textarea data-testid="offer-desc-input" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 w-full resize-none rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Gate</label>
              <input data-testid="offer-gate-input" value={form.gate} onChange={(e) => set("gate", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Class</label>
              <input data-testid="offer-class-input" value={form.flight_class} onChange={(e) => set("flight_class", e.target.value)} className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Expiry</label>
              <input data-testid="offer-expiry-input" value={form.expiry} onChange={(e) => set("expiry", e.target.value)} placeholder="31 DEC 2026" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500" />
            </div>
          </div>
          <label className="flex items-center gap-2">
            <input data-testid="offer-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-amber-600" />
            <span className="text-sm text-stone-700">Active (visible on site)</span>
          </label>
        </div>
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#E7DFD3] py-2.5 text-sm text-stone-600">Cancel</button>
          <button data-testid="offer-save-button" onClick={save} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-full gold-gradient py-2.5 text-sm font-semibold text-stone-900 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- DASHBOARD */
function Dashboard() {
  const { logout } = useAuth();
  const { settings, loadSettings, loadOffers } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [editProduct, setEditProduct] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [editOffer, setEditOffer] = useState(undefined);
  const [form, setForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const loadProducts = () => api.get("/admin/products").then(({ data }) => setProducts(data));
  const loadAllOffers = () => api.get("/offers").then(({ data }) => setOffers(data));
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  const loadCustomers = () => api.get("/admin/customers").then(({ data }) => setCustomers(data));
  const loadInventory = () => api.get("/admin/inventory/history").then(({ data }) => setInventory(data));
  const loadNotifications = () => api.get("/admin/notifications").then(({ data }) => setNotifications(data));

  useEffect(() => {
    loadProducts(); loadAllOffers(); loadOrders(); loadCustomers(); loadInventory(); loadNotifications();
  }, []);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadProducts();
  };
  const delOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/offers/${id}`);
    toast.success("Offer deleted");
    loadAllOffers();
    loadOffers();
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", form);
      await loadSettings();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
    setSavingSettings(false);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateOrderStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success(`Order → ${status}`);
    loadOrders(); loadNotifications();
  };

  const TABS = [
    ["analytics", "Analytics", BarChart3],
    ["products", "Products", Package],
    ["orders", "Orders", ShoppingCart],
    ["inventory", "Inventory", Boxes],
    ["customers", "Customers", Users],
    ["collections", "Collections", Layers],
    ["coupons", "Coupons", Ticket],
    ["reviews", "Reviews", MessageSquare],
    ["testimonials", "Testimonials", Quote],
    ["returns", "Returns", Boxes],
    ["banners", "Banners", ImageIcon],
    ["offers", "Offers", Ticket],
    ["notifications", "Alerts", Bell],
    ["brand", "Brand", Palette],
    ["payments", "Payments", CreditCard],
    ["settings", "Content", SettingsIcon],
    ["export", "Export", Download],
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* topbar */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-[#E7DFD3] bg-[#2C221E] px-4 py-3 text-cream sm:px-6">
        <div className="flex items-center gap-3">
          <AirplaneLogo size={34} />
          <div>
            <p className="font-serif text-xl text-[#F4E7C5] leading-none">PRIYARAA</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-amber-500/70">Control Tower</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-view-site" onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs hover:border-amber-500">
            <ExternalLink size={14} /> View Site
          </button>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {/* tabs */}
        <div className="flex gap-2 overflow-x-auto">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                tab === key ? "gold-gradient text-stone-900" : "border border-[#E7DFD3] bg-white text-stone-600"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* PRODUCTS */}
        {tab === "products" && (
          <div className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-stone-900">Products ({products.length})</h2>
              <button data-testid="add-product-button" onClick={() => setEditProduct(null)} className="flex items-center gap-2 rounded-full gold-gradient px-4 py-2 text-sm font-semibold text-stone-900">
                <Plus size={16} /> Add Product
              </button>
            </div>
            <div className="overflow-hidden rounded-xl border border-[#E7DFD3] bg-white">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-3 border-b border-[#EFE8DC] p-3 last:border-0">
                  <img src={resolveImg(p.image_url)} alt={p.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-lg text-stone-900">{p.name}
                      {p.status === "draft" && <span className="ml-2 rounded bg-stone-200 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-stone-600">Draft</span>}
                    </p>
                    <p className="text-xs text-stone-500">
                      {p.category} · {formatPrice(p.sale_price || p.price)} {p.featured && "· ★"}
                      {" · "}
                      <span className={p.stock <= 0 ? "text-red-600 font-semibold" : p.stock <= (p.low_stock_threshold || 3) ? "text-amber-600 font-semibold" : ""}>
                        {p.stock <= 0 ? "Out of stock" : `${p.stock} in stock`}
                      </span>
                    </p>
                  </div>
                  <button data-testid={`edit-product-${p.id}`} onClick={() => setEditProduct(p)} className="rounded-lg border border-[#E7DFD3] p-2 text-stone-600 hover:border-amber-500"><Pencil size={15} /></button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => delProduct(p.id)} className="rounded-lg border border-[#E7DFD3] p-2 text-red-500 hover:border-red-400"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* OFFERS */}
        {tab === "offers" && (
          <div className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-stone-900">Offers ({offers.length})</h2>
              <button data-testid="add-offer-button" onClick={() => setEditOffer(null)} className="flex items-center gap-2 rounded-full gold-gradient px-4 py-2 text-sm font-semibold text-stone-900">
                <Plus size={16} /> Add Offer
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {offers.map((o) => (
                <div key={o.id} data-testid={`admin-offer-row-${o.id}`} className="rounded-xl border border-[#E7DFD3] bg-white p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-serif text-xl text-stone-900">{o.discount} <span className="text-sm text-amber-700">{o.title}</span></p>
                      <p className="mt-1 font-mono text-xs text-stone-500">{o.promo_code} · Gate {o.gate} · {o.active ? "Active" : "Hidden"}</p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`edit-offer-${o.id}`} onClick={() => setEditOffer(o)} className="rounded-lg border border-[#E7DFD3] p-2 text-stone-600 hover:border-amber-500"><Pencil size={15} /></button>
                      <button data-testid={`delete-offer-${o.id}`} onClick={() => delOffer(o.id)} className="rounded-lg border border-[#E7DFD3] p-2 text-red-500 hover:border-red-400"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <div className="mt-6" data-testid="admin-orders">
            <h2 className="mb-4 font-serif text-2xl text-stone-900">Orders ({orders.length})</h2>
            {orders.length === 0 && <p className="text-stone-500">No orders yet.</p>}
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="rounded-xl border border-[#E7DFD3] bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-mono text-sm text-stone-900">{o.order_number}</p>
                      <p className="text-xs text-stone-500">{o.customer_name} · {o.customer_email} · {o.items.length} item(s) · {formatPrice(o.total)}{o.gift?.is_gift && " · 🎁 Gift"}</p>
                      <p className="text-xs text-stone-400">{o.address?.line1}, {o.address?.city} - {o.address?.pincode} · {new Date(o.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    <select data-testid={`order-status-${o.order_number}`} value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500">
                      {ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CUSTOMERS */}
        {tab === "customers" && (
          <div className="mt-6" data-testid="admin-customers">
            <h2 className="mb-4 font-serif text-2xl text-stone-900">Customers ({customers.length})</h2>
            <div className="overflow-hidden rounded-xl border border-[#E7DFD3] bg-white">
              {customers.length === 0 && <p className="p-4 text-stone-500">No customers yet.</p>}
              {customers.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-[#EFE8DC] p-3 last:border-0">
                  <div>
                    <p className="font-medium text-stone-900">{c.name}</p>
                    <p className="text-xs text-stone-500">{c.email} · {c.phone || "—"}</p>
                  </div>
                  <span className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700">{c.order_count} order(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* INVENTORY */}
        {tab === "inventory" && (
          <div className="mt-6" data-testid="admin-inventory">
            <h2 className="mb-4 font-serif text-2xl text-stone-900">Inventory</h2>
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <p className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle size={16} /> Low / out of stock</p>
              <div className="mt-2 space-y-1">
                {products.filter((p) => p.stock <= (p.low_stock_threshold || 3)).map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="text-stone-700">{p.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={p.stock <= 0 ? "text-red-600 font-semibold" : "text-amber-700"}>{p.stock} left</span>
                      <input type="number" defaultValue={p.stock} data-testid={`stock-input-${p.id}`} className="w-16 rounded border border-[#E7DFD3] px-2 py-1" id={`stk-${p.id}`} />
                      <button data-testid={`stock-save-${p.id}`} onClick={async () => { const v = Number(document.getElementById(`stk-${p.id}`).value); await api.put(`/admin/products/${p.id}/stock`, { stock: v }); toast.success("Stock updated"); loadProducts(); loadInventory(); }} className="rounded bg-amber-600 px-2 py-1 text-xs text-white">Set</button>
                    </div>
                  </div>
                ))}
                {products.filter((p) => p.stock <= (p.low_stock_threshold || 3)).length === 0 && <p className="text-sm text-amber-700">All good — no low stock.</p>}
              </div>
            </div>
            <h3 className="mb-2 font-serif text-lg text-stone-900">Stock History</h3>
            <div className="overflow-hidden rounded-xl border border-[#E7DFD3] bg-white">
              {inventory.length === 0 && <p className="p-4 text-stone-500">No movements yet.</p>}
              {inventory.slice(0, 50).map((h) => (
                <div key={h.id} className="flex items-center justify-between border-b border-[#EFE8DC] p-3 text-sm last:border-0">
                  <span className="text-stone-700">{h.product_name}</span>
                  <span className="text-stone-400">{h.reason}</span>
                  <span className={h.change < 0 ? "text-red-600" : "text-green-600"}>{h.change > 0 ? "+" : ""}{h.change} → {h.stock_after}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NOTIFICATIONS */}
        {tab === "notifications" && (
          <div className="mt-6" data-testid="admin-notifications">
            <h2 className="mb-4 font-serif text-2xl text-stone-900">Alerts & Events ({notifications.length})</h2>
            <div className="space-y-2">
              {notifications.length === 0 && <p className="text-stone-500">No events yet.</p>}
              {notifications.map((n) => (
                <div key={n.id} className="flex items-start gap-3 rounded-xl border border-[#E7DFD3] bg-white p-3">
                  <Bell size={16} className="mt-0.5 text-amber-600" />
                  <div>
                    <p className="text-sm font-medium text-stone-900">{n.title}</p>
                    <p className="text-xs text-stone-500">{n.body} · {new Date(n.created_at).toLocaleString("en-IN")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "analytics" && <AnalyticsPanel />}
        {tab === "brand" && <BrandPanel />}
        {tab === "coupons" && <CouponsPanel />}
        {tab === "collections" && <CollectionsPanel />}
        {tab === "reviews" && <ReviewsPanel />}
        {tab === "testimonials" && <TestimonialsPanel />}
        {tab === "returns" && <ReturnsPanel />}
        {tab === "banners" && <BannersPanel />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "export" && <ExportPanel />}
        {tab === "settings" && <SectionsManager />}

        {/* SETTINGS */}
        {tab === "settings" && form && (
          <div className="mt-10 max-w-2xl" data-testid="finishes-images">
            <h2 className="mb-1 font-serif text-2xl text-stone-900">Finishes Images</h2>
            <p className="mb-4 text-xs text-stone-500">Images for the "Choose Your Journey" cards on the homepage. If empty, a soft beige card with the finish name is shown.</p>
            <div className="space-y-5 rounded-xl border border-[#E7DFD3] bg-white p-6">
              <SingleImageField label="Anti-Tarnish Image" value={form.segment_at_image} onChange={(v) => setF("segment_at_image", v)} testid="finish-anti-tarnish" />
              <div><label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Anti-Tarnish Alt Text (describes the image for Google & screen readers)</label><input data-testid="segment_at_alt-input" value={form.segment_at_alt || ""} onChange={(e) => setF("segment_at_alt", e.target.value)} placeholder="e.g. Gold-tone anti-tarnish hoops on beige linen" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500" /></div>
              <SingleImageField label="Oxidised Image" value={form.segment_ox_image} onChange={(v) => setF("segment_ox_image", v)} testid="finish-oxidised" />
              <div><label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Oxidised Alt Text (describes the image for Google & screen readers)</label><input data-testid="segment_ox_alt-input" value={form.segment_ox_alt || ""} onChange={(e) => setF("segment_ox_alt", e.target.value)} placeholder="e.g. Gold-tone anti-tarnish hoops on beige linen" className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 text-sm outline-none focus:border-amber-500" /></div>
              <button data-testid="save-finishes-button" onClick={saveSettings} disabled={savingSettings} className="flex items-center gap-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold text-stone-900 disabled:opacity-60">
                {savingSettings && <Loader2 size={16} className="animate-spin" />} Save Finishes Images
              </button>
            </div>
          </div>
        )}
        {tab === "settings" && form && (
          <div className="mt-6 max-w-2xl">
            <h2 className="mb-4 font-serif text-2xl text-stone-900">Brand & Contact</h2>
            <div className="space-y-4 rounded-xl border border-[#E7DFD3] bg-white p-6">
              {[
                ["whatsapp_number", "WhatsApp Number (with country code, no +)", "919999999999"],
                ["instagram_url", "Instagram URL", "https://instagram.com/priyaraa"],
                ["email", "Support Email", "hello@priyaraa.com"],
                ["phone", "Phone (display)", "+91 99999 99999"],
                ["announcement", "Announcement Bar Text", ""],
                ["hero_headline", "Hero Headline", ""],
                ["hero_subheading", "Hero Subheading", ""],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">{label}</label>
                  <input
                    data-testid={`settings-${k}-input`}
                    value={form[k] || ""}
                    onChange={(e) => setF(k, e.target.value)}
                    placeholder={ph}
                    className="mt-1 w-full rounded-lg border border-[#E7DFD3] px-3 py-2 outline-none focus:border-amber-500"
                  />
                </div>
              ))}
              <SingleImageField value={form.hero_image} onChange={(v) => setF("hero_image", v)} testid="settings-hero-image" />
              <button data-testid="save-settings-button" onClick={saveSettings} disabled={savingSettings} className="flex items-center gap-2 rounded-full gold-gradient px-6 py-2.5 text-sm font-semibold text-stone-900 disabled:opacity-60">
                {savingSettings && <Loader2 size={16} className="animate-spin" />} Save Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {editProduct !== undefined && (
        <ProductModal product={editProduct} onClose={() => setEditProduct(undefined)} onSaved={() => { setEditProduct(undefined); loadProducts(); }} />
      )}
      {editOffer !== undefined && (
        <OfferModal offer={editOffer} onClose={() => setEditOffer(undefined)} onSaved={() => { setEditOffer(undefined); loadAllOffers(); loadOffers(); }} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- ENTRY */
export default function Admin() {
  const { user, ready } = useAuth();

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C221E]">
        <Loader2 size={32} className="animate-spin text-amber-500" />
      </div>
    );
  }
  return user ? <Dashboard /> : <Login />;
}
